import { watch } from 'vue';
import { countNights, parseDate, toISO, today, type StayDates } from '@/entities/searchCriteria';
import { SearchFormGoalName, type SearchFormGoal } from '@/entities/searchForm';
import type { UseStayDates } from './types';
import { getFlexibleClickGoal } from '../lib/flexibleClickGoal';

export interface UseStayDatesTrackingOptions {
  stayDates: UseStayDates;
  track: (goal: SearchFormGoal) => void;
}

export interface UseStayDatesTracking {
  toggle: () => void;
  close: () => void;
  clickDate: (iso: string) => boolean;
  setFlexible: (daysFlexible: number) => void;
  validate: () => boolean;
}

function getLeadDays(checkIn: string): string {
  return String(countNights(toISO(today()), checkIn));
}

export function useStayDatesTracking(options: UseStayDatesTrackingOptions): UseStayDatesTracking {
  const { stayDates, track } = options;
  let clicksCount = 0;
  let openedStay: StayDates = stayDates.stay.value;
  let hasUserRange = false;

  const trackOpen = (): void => {
    clicksCount = 0;
    openedStay = stayDates.stay.value;
    track({ name: SearchFormGoalName.HotelCalendarOpen, params: {} });
  };

  const trackClose = (): void => {
    const { checkIn, nights } = stayDates.stay.value;
    const hasPendingCheckIn = Boolean(stayDates.pendingCheckIn.value);
    const isRangeChanged = checkIn !== openedStay.checkIn || nights !== openedStay.nights;
    track({
      name: SearchFormGoalName.HotelCalendarClose,
      params: {
        dates_selected: hasPendingCheckIn ? '1' : '2',
        is_changed: String(hasPendingCheckIn || isRangeChanged),
      },
    });
  };

  const trackNoCheckOutError = (trigger: 'close' | 'search_click'): void => {
    track({ name: SearchFormGoalName.HotelCalendarErrorNoCheckOut, params: { trigger } });
  };

  watch(stayDates.controlActivated, (isOpen) => (isOpen ? trackOpen() : trackClose()), { flush: 'sync' });

  const close = (): void => {
    const hasPendingCheckIn = Boolean(stayDates.pendingCheckIn.value);
    stayDates.close();
    if (hasPendingCheckIn) {
      trackNoCheckOutError('close');
    }
  };

  const toggle = (): void => {
    if (stayDates.controlActivated.value) {
      close();
      return;
    }
    stayDates.open();
  };

  const clickDate = (iso: string): boolean => {
    const checkInBefore = stayDates.pendingCheckIn.value;
    const isComplete = stayDates.clickDate(iso);
    clicksCount += 1;

    if (isComplete && checkInBefore) {
      hasUserRange = true;
      track({
        name: SearchFormGoalName.HotelCalendarCheckOutSelect,
        params: {
          nights: String(countNights(checkInBefore, toISO(parseDate(iso)))),
          lead_days: getLeadDays(checkInBefore),
          flex_days: String(stayDates.stay.value.flexible),
          clicks_count: String(clicksCount),
        },
      });
      return isComplete;
    }

    const checkIn = stayDates.pendingCheckIn.value;
    if (checkIn && checkIn !== checkInBefore) {
      track({
        name: SearchFormGoalName.HotelCalendarCheckInSelect,
        params: { lead_days: getLeadDays(checkIn), is_reset: String(hasUserRange && !checkInBefore) },
      });
    }
    return isComplete;
  };

  const setFlexible = (daysFlexible: number): void => {
    const goal = getFlexibleClickGoal(daysFlexible, stayDates.stay.value.flexible);
    stayDates.setFlexible(daysFlexible);
    if (goal) {
      track(goal);
    }
  };

  const validate = (): boolean => {
    const isValid = stayDates.validate();
    if (!isValid) {
      trackNoCheckOutError('search_click');
    }
    return isValid;
  };

  return { toggle, close, clickDate, setFlexible, validate };
}
